/* eslint-disable camelcase */
import { useEffect, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import crud from '../../../../api/crud';
import ItemList from '../../global/ItemList';
import ListHeaders from '../../global/ListHeaders';
import LoadingBar from '../../global/LoadingBar';
import ListPaginator from '../../global/ListPaginator';
import { TBB_LIST_BY_USER } from '../../config/service';
import { useAlert } from '../../../providers/AlertProvider';
import { ANCHOR_MY_TBB, ROUTE_HOME } from '../../config/routes';
import GenericBanner from '../../global/Banner/GenericBanner';
import {
  HOW_WE_RECYCLE_URL,
  TAKE_BACK_BAG_COLLECTION_URL,
  TAKE_BACK_BAG_RAINBOW_3_PACK_URL,
  TAKE_BACK_BAG_URL,
  TECH_BACK_BOX_MEDIUM_URL,
} from '../../config/externalURL';
import { appendUtmParams } from '../../utils/url';
import TBMediumBox from '../../images/tbb-midium-box.webp';
import TBBIphones from '../../images/tech-tbb-iphones.webp';
import TBBSingle from '../../images/tbb-single.png';
import TBB3Pack from '../../images/tbb-3-pack.png';
import EmptyLabelAsset from '../../images/tbb-empty-label.svg';
import BackArrowFigmaIcon from '../../images/back-arrow.svg';
import EmptyEllipseAsset from '../../images/tbb-empty-ellipse.svg';
import EmptyDashedBoxAsset from '../../images/tbb-empty-dashed-box.svg';

import DashboardSection from '../../global/DashboardSection';
import TakeBackBagsListItem from '../../global/TakeBackBagsListItem';
import Slider, { SLIDER_TYPES } from '../../global/Slider';
import UnlimitedShortcutBannerCard from './UnlimitedShortcutBannerCard';

import { getTrackerId, POSTHOG_CAPTURE_ATTRIBUTES, TRACKER_IDS } from '../../config/tracker';
import { useFirebaseAuth } from '../../../global/FirebaseProvider/FirebaseProvider';

import './TakeBackBags.scss';

const PAGE_SIZE = 10;
const TBB_ITEMS = [
  {
    id: 1,
    label: 'The single',
    url: TAKE_BACK_BAG_URL,
    imgUrl: TBBSingle,
    bgColor: '#9AD2FF',
  },
  {
    id: 2,
    label: 'The 3-pack',
    url: TAKE_BACK_BAG_RAINBOW_3_PACK_URL,
    imgUrl: TBB3Pack,
    bgColor: '#D8C6FF',
  },
  {
    id: 3,
    label: 'Medium Tech Take Back Box',
    url: TECH_BACK_BOX_MEDIUM_URL,
    imgUrl: TBMediumBox,
    bgColor: '#ADD6FF',
  },
];

const {
  recycle: {
    banner: {
      bannerLink: trackerBannerLink,
    },
    shop: {
      tbbItem: trackerTbbItem,
    },
  },
} = TRACKER_IDS;

const itemListHeader = {
  title: 'My shipping labels',
  sortedText: 'Most recently registered',
};

const recycleContent = {
  title: 'How we recycle',
  description: 'Learn more about our innovative sorting and grading system that enables getting unwanted clothing and e-waste to the right place.',
  ctaLabel: 'Learn more',
  ctaURL: HOW_WE_RECYCLE_URL,
  srcImage: TBBIphones,
};

function parseTbbData(redemption) {
  const { labeldata, recyclingProduct, ...rest } = redemption;
  let parsedRedemption = {
    recyclingProductTypeId: recyclingProduct?.recyclingProductTypeId,
    ...rest,
  };

  if (labeldata) {
    parsedRedemption = {
      ...parsedRedemption,
      labeldata: JSON.parse(labeldata),
    };
  }

  return parsedRedemption;
}

const TakeBackBags = () => {
  const history = useHistory();
  const {
    isLoading: isLoadingFirebaseAuth,
    isSignedIn,
    userData,
  } = useFirebaseAuth();

  const [tbbData, setTbbData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const setAlert = useAlert();
  const isLoading = isLoadingFirebaseAuth || loading;

  const showShopCTA = isSignedIn && !userData?.activeSubscription;
  const handleBack = useCallback(() => {
    const hasBrowserHistory = typeof window !== 'undefined' && window.history.length > 1;
    const hasSameOriginReferrer = (() => {
      if (typeof document === 'undefined' || !document.referrer) {
        return false;
      }
      try {
        return new URL(document.referrer).origin === window.location.origin;
      } catch (error) {
        return false;
      }
    })();

    if (hasBrowserHistory && hasSameOriginReferrer) {
      history.goBack();
      return;
    }

    history.push(ROUTE_HOME);
  }, [history]);

  const userTbb = useCallback(
    async () => {
      setLoading(true);
      try {
        const { count, result } = await crud.get({ path: `${TBB_LIST_BY_USER}?page=${currentPage}&pageSize=${PAGE_SIZE}` });
        const data = result.map((redemption) => parseTbbData(redemption));
        setTbbData(data);
        setTotalRecords(count);
      } catch (error) {
        setAlert({
          type: 'alert',
          message: error?.error?.message,
          error: error?.error?.message,
        });
      }

      setLoading(false);
    },
    [currentPage],
  );

  useEffect(() => {
    userTbb();
  }, [currentPage]);

  return (
    <div id={ANCHOR_MY_TBB} className="TakeBackBags">
      <div className="TakeBackBags__container">

        {showShopCTA && tbbData.length > 0 && (
          <>
            <UnlimitedShortcutBannerCard
              trackerProps={{
                [POSTHOG_CAPTURE_ATTRIBUTES.TRACKER_ID]: getTrackerId(trackerBannerLink),
                [POSTHOG_CAPTURE_ATTRIBUTES.TBB_SHOP_LINK]: 'true',
              }}
            />
            <DashboardSection
              title="Shop the TBB"
              onClick={() => {
                window.location.href = appendUtmParams(TAKE_BACK_BAG_COLLECTION_URL);
              }}
              contained
            >
              <div className="TakeBackBags__slider">
                <Slider
                  sliderId="tbbSlider"
                  type={SLIDER_TYPES.OVERFLOW}
                  autoplay={false}
                  rewind={false}
                  paginationDots={false}
                  gap={16}
                  slidesPerPage={TBB_ITEMS.length}
                  slides={TBB_ITEMS.map((tbbItem) => ({
                    key: `tbb-${tbbItem.id}`,
                    slide: (
                      <TakeBackBagsListItem
                        key={`tbb-${tbbItem.id}`}
                        tbbItem={tbbItem}
                        trackerProps={{
                          [POSTHOG_CAPTURE_ATTRIBUTES.TRACKER_ID]: getTrackerId(trackerTbbItem),
                          [POSTHOG_CAPTURE_ATTRIBUTES.TBB_SHOP_LINK]: 'true',
                        }}
                      />),
                  }))}
                />
              </div>
            </DashboardSection>
          </>
        )}
        {isLoading && <LoadingBar />}
        {!isLoading && tbbData.length === 0 && (
          <div className="TakeBackBags__empty">
            <div className="TakeBackBags__empty-header">
              <button
                type="button"
                className="TakeBackBags__empty-back"
                aria-label="Go back"
                onClick={handleBack}
              >
                <img src={BackArrowFigmaIcon} alt="" aria-hidden="true" />
              </button>
              <div className="TakeBackBags__empty-header-title">Shipping Labels</div>
            </div>
            <div className="TakeBackBags__empty-content">
              <div className="TakeBackBags__empty-main">
                <div className="TakeBackBags__empty-illustration">
                  <img
                    src={EmptyEllipseAsset}
                    alt=""
                    aria-hidden="true"
                    className="TakeBackBags__empty-illustration-ellipse"
                  />
                  <img
                    src={EmptyDashedBoxAsset}
                    alt=""
                    aria-hidden="true"
                    className="TakeBackBags__empty-illustration-dashed-box"
                  />
                  <img
                    src={EmptyLabelAsset}
                    alt=""
                    aria-hidden="true"
                    className="TakeBackBags__empty-illustration-label"
                  />
                </div>
                <div className="TakeBackBags__empty-text">
                  <h2 className="TakeBackBags__empty-title">
                    You haven&apos;t ordered any
                    <br />
                    {' '}
                    Take Back Bags or Boxes yet.
                  </h2>
                  <p className="TakeBackBags__empty-subtitle">
                    Start your clean out journey!
                  </p>
                </div>
              </div>
              <a
                href={appendUtmParams(TAKE_BACK_BAG_COLLECTION_URL)}
                className="TakeBackBags__empty-cta"
              >
                Order Bags & Boxes
              </a>
            </div>
          </div>
        )}
        {!isLoading && tbbData.length > 0 && (
          <>
            <ListHeaders
              titleSectionClassName="TitleSectionAccount_titleTransForm"
              title={itemListHeader.title}
              sortedText={itemListHeader.sortedText}
            />
            <div className="TakeBackBags__items">
              <ItemList dataList={tbbData} setTbbData={setTbbData} />
              {totalRecords > PAGE_SIZE && (
                <ListPaginator
                  pageSize={PAGE_SIZE}
                  totalItems={totalRecords}
                  currentPage={currentPage}
                  onPageSelection={(selectedPage) => {
                    setCurrentPage(selectedPage);
                  }}
                />
              )}
            </div>
          </>
        )}
        <GenericBanner
          content={recycleContent}
        />
      </div>
    </div>

  );
};

export default TakeBackBags;
